/**
 * Factory utility for creating AppSync subscription hooks with shared boilerplate.
 * Handles singleton management, ref stability, data validation, debug logging, and cleanup.
 *
 * Usage:
 * ```ts
 * export const useMySubscription = createSubscriptionHook('MySubscription', [
 *   { query: onMyEvent, eventType: 'onMyEvent', invalidateKeys: [myQueryKeys.all] },
 * ]);
 * ```
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  OnDeleteKPIVariantSubscription,
  OnLockDeckSubscription,
  OnLockPageSubscription,
  OnReleaseDeckLockSubscription,
  OnReleasePageLockSubscription,
  OnUpdateDeckSubscription,
  OnUpdateKPIVariantSubscription,
  OnUpdateRootKPISubscription,
} from '@services/api/deckManagement/graphql/schema/API';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

/** Union of all valid subscription event type keys, derived from generated API types */
type SubscriptionEventType =
  | keyof OnLockDeckSubscription
  | keyof OnReleaseDeckLockSubscription
  | keyof OnUpdateDeckSubscription
  | keyof OnUpdateKPIVariantSubscription
  | keyof OnDeleteKPIVariantSubscription
  | keyof OnUpdateRootKPISubscription
  | keyof OnLockPageSubscription
  | keyof OnReleasePageLockSubscription;

/**
 * Configuration for a single subscription within a hook.
 */
export interface SubscriptionDefinition {
  /** GraphQL subscription query string */
  query: string;
  /** Event type key in the subscription response (e.g. 'onUpdateKPIVariant') */
  eventType: SubscriptionEventType;
  /** Static query keys to invalidate when this event fires */
  invalidateKeys?: QueryKey[];
  /** Dynamic query keys resolved at event time using the scope key (e.g. deckId) */
  getInvalidateKeys?: (scopeKey: string) => QueryKey[];
}

/** Internal state tracking for active subscriptions */
interface SubscriptionState {
  key: string;
  subscriptions: { unsubscribe: () => void }[];
}

/**
 * Identifiers for subscription hooks, used for debug/error logging.
 */
export enum SubscriptionHookName {
  DeckSubscription = 'DeckSubscription',
  KPISubscription = 'KPISubscription',
  PageLockSubscription = 'PageLockSubscription',
}

/**
 * Validates subscription data for a specific event type.
 * Ensures the data object exists, is an object, and contains a truthy value for the given event key.
 * @param data - Raw subscription data from AppSync
 * @param eventType - Expected event type property name (e.g. 'onLockDeck', 'onReleaseDeck', 'onUpdateKPIVariant')
 * @returns True if data contains a valid event of the specified type
 */
export const isValidSubscriptionData = (data: unknown, eventType: SubscriptionEventType): boolean => {
  return !!(data && typeof data === 'object' && eventType in data && (data as Record<string, unknown>)[eventType]);
};

/**
 * Creates a reusable subscription hook with singleton management.
 *
 * Each hook created by this factory maintains its own singleton — only one set of WebSocket
 * subscriptions will be active at a time per hook instance. If `scopeKey` changes (e.g. switching
 * decks), existing subscriptions are torn down and new ones are created.
 *
 * @param hookName - Identifier used for debug/error logging
 * @param definitions - Array of subscription configurations
 * @returns A React hook function accepting `{ enabled, scopeKey }` params
 */
export const createSubscriptionHook = (hookName: SubscriptionHookName, definitions: SubscriptionDefinition[]) => {
  // Singleton state — scoped per factory invocation (one per hook)
  let activeSubscriptions: SubscriptionState | null = null;

  /** Unsubscribes all active subscriptions and resets state */
  const cleanup = () => {
    if (activeSubscriptions) {
      activeSubscriptions.subscriptions.forEach((sub) => sub.unsubscribe());
      activeSubscriptions = null;
    }
  };

  /**
   * @param params.enabled - Whether the subscription should be active
   * @param params.scopeKey - Scope identifier for singleton management (e.g. deckId). Defaults to 'global'.
   * @param params.variables - Optional variables to pass to all subscription queries
   */
  return (params: { enabled?: boolean; scopeKey?: string; variables?: Record<string, unknown> }): void => {
    const { enabled = false, scopeKey = 'global', variables } = params;
    const queryClient = useQueryClient();
    const { deckManagementSubscriptionClient } = useApiClients();

    // Use refs to avoid recreating subscriptions when these stable objects change reference
    const queryClientRef = useRef(queryClient);
    const subscriptionClientRef = useRef(deckManagementSubscriptionClient);
    queryClientRef.current = queryClient;
    subscriptionClientRef.current = deckManagementSubscriptionClient;

    useEffect(() => {
      if (!enabled) return undefined;

      // If subscriptions already exist for this scope, don't create new ones
      if (activeSubscriptions?.key === scopeKey) return undefined;

      // Clean up existing subscriptions if scope changed
      cleanup();

      const subs = definitions.map(({ query, eventType, invalidateKeys, getInvalidateKeys }) =>
        subscriptionClientRef.current.graphql({ query, ...(variables && { variables }) }).subscribe({
          next: ({ data }) => {
            console.debug(`[${hookName}] ${eventType} data received:`, data);
            if (isValidSubscriptionData(data, eventType)) {
              const keys = getInvalidateKeys?.(scopeKey) ?? invalidateKeys ?? [];
              keys.forEach((key) => {
                queryClientRef.current.invalidateQueries({ queryKey: key });
              });
            }
          },
          error: (error) => {
            console.error(`[${hookName}] ${eventType} subscription error:`, error);
          },
        }),
      );

      activeSubscriptions = { key: scopeKey, subscriptions: subs };

      // Cleanup subscriptions on unmount or dependency change
      return () => cleanup();
      // eslint-disable-next-line @eslint-react/exhaustive-deps -- variables is intentionally excluded; scopeKey controls reconnection
    }, [enabled, scopeKey]);
  };
};
